import { useTranslation } from 'react-i18next';
import { colors } from 'components/styles/variables';
import { times } from 'lib/utils';

const NUM_BARS = 5;

function FullBar(): JSX.Element {
  return (
    <div data-rating-bar="full" className="dealer-rating-full-bar">
      <style jsx>{`
        .dealer-rating-full-bar {
          width: 22px;
          height: 6px;
          margin-right: 3px;
          background-color: ${colors.ALR1};
          border-radius: 1.5px;
        }
      `}</style>
    </div>
  );
}

function HalfBar(): JSX.Element {
  return (
    <div data-rating-bar="half" className="dealer-rating-half-bar">
      <div className="dealer-rating-half-bar filled" />
      <style jsx>{`
        .dealer-rating-half-bar {
          width: 22px;
          height: 6px;
          margin-right: 3px;
          background-color: ${colors.G2};
          border-radius: 1.5px;
        }

        .filled {
          width: 50%;
          background-color: ${colors.ALR1};
        }
      `}</style>
    </div>
  );
}

function EmptyBar(): JSX.Element {
  return (
    <div data-rating-bar="empty" className="dealer-rating-empty-bar">
      <style jsx>{`
        .dealer-rating-empty-bar {
          width: 22px;
          height: 6px;
          margin-right: 3px;
          background-color: ${colors.G2};
          border-radius: 1.5px;
        }
      `}</style>
    </div>
  );
}

function renderBar({
  value,
  currentBarRating,
}: {
  value: number;
  currentBarRating: number;
}): JSX.Element {
  if (value >= currentBarRating) {
    return <FullBar key={currentBarRating} />;
  }

  if (value < currentBarRating && value > currentBarRating - 1) {
    return <HalfBar key={currentBarRating} />;
  }

  return <EmptyBar key={currentBarRating} />;
}

interface RatingProps {
  value: number;
  label: string;
}

function Rating({ value, label }: RatingProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="bar-container" key={label} data-rating-bar-category={label}>
      <div className="bar-rating">
        {times<number>(NUM_BARS).map((index) => {
          return renderBar({ value, currentBarRating: index + 1 });
        })}
      </div>
      <div className="bar-label">{t(`dealerPages.ratingCategories.${label}`)}</div>
      <style jsx>{`
        .bar-container {
          display: flex;
          flex-direction: row;
          margin-bottom: 7px;
        }

        .bar-container:last-child {
          margin-bottom: 0;
        }

        .bar-rating {
          width: 124px;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }

        .bar-label {
          text-transform: uppercase;
          font-weight: 900;
          font-size: 14px;
          padding-left: 10px;
        }
      `}</style>
    </div>
  );
}

interface RatingBreakdownProps {
  pricing: number;
  service: number;
  helpfulness: number;
  responsiveness: number;
}

export default function RatingBreakdown(categories: RatingBreakdownProps): JSX.Element {
  const categoryNames = ['pricing', 'service', 'helpfulness', 'responsiveness'];

  return (
    <div className="rating-breakdown-container">
      {categoryNames.map((name) => {
        const value = categories[name as keyof RatingBreakdownProps];
        return <Rating value={value} label={name} key={name} />;
      })}
    </div>
  );
}
